import { Modal } from 'antd';
import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router';

import Button from '@/components/ui/Button';
import { useUserActivity } from '@/features/auth/hooks/use-user-activity';
import { useUserActivityStore } from '@/store/use-user-activity-store';

import { useEmbeddedLogout } from '../hooks';

const IDLE_TIMEOUT = 4 * 60 * 1000;
const WARNING_DURATION = 1 * 60 * 1000;

export function SessionWarning() {
  const { t } = useTranslation();

  const { setLastActivityTime, lastActivitySyncTime } = useUserActivityStore();
  useUserActivity();

  const navigate = useNavigate();
  const [isWarningOpen, setWarningOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(WARNING_DURATION / 1000);
  const { mutateAsync: logout } = useEmbeddedLogout();
  const countdownInterval = useRef<NodeJS.Timeout | null>(null);

  const clearCountdown = () => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
  };

  const handleLogout = useCallback(async () => {
    clearCountdown();
    await logout();
    navigate(0);
  }, [logout, navigate]);

  const startCountdown = () => {
    clearCountdown();
    countdownInterval.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const onPrompt = () => {
    const now = Date.now();
    const timeSinceLastSync = now - (lastActivitySyncTime ?? 0);

    if (timeSinceLastSync < WARNING_DURATION) {
      return;
    }

    setWarningOpen(true);
    setTimeLeft(WARNING_DURATION / 1000);
    startCountdown();
  };

  const onIdle = () => {
    handleLogout();
  };

  const onActive = () => {
    if (isWarningOpen) {
      setLastActivityTime(Date.now());
      setWarningOpen(false);
      setTimeLeft(WARNING_DURATION / 1000);
      clearCountdown();
    }
  };

  const onAction = () => {
    setLastActivityTime(Date.now());
  };

  const { activate } = useIdleTimer({
    timeout: IDLE_TIMEOUT,
    promptBeforeIdle: WARNING_DURATION,
    onPrompt,
    onIdle,
    onAction,
    crossTab: true,
    syncTimers: 200,
    leaderElection: true,
    events: ['keydown', 'mousedown', 'touchstart', 'MSPointerDown', 'visibilitychange'],
  });

  const handleContinue = () => {
    setWarningOpen(false);
    setTimeLeft(WARNING_DURATION / 1000);
    clearCountdown();
    activate();
  };

  return (
    <Modal
      title={t('session.timeout.title', { ns: 'common' })}
      open={isWarningOpen}
      footer={
        <div className="flex md:flex-row flex-col gap-2 w-full">
          <Button key="continue" onClick={handleContinue} className="flex-1" variant="primary">
            {t('session.timeout.continue', { ns: 'common' })}
          </Button>
          <Button key="logout" onClick={handleLogout} className="flex-1" variant="danger">
            {t('session.timeout.logout', { ns: 'common' })}
          </Button>
        </div>
      }
      onCancel={handleLogout}
      onClose={onActive}
    >
      <p>{t('session.timeout.message', { ns: 'common' })}</p>
      <p className="mt-2 font-bold text-red-500">{t('session.timeout.countdown', { ns: 'common', seconds: timeLeft })}</p>
    </Modal>
  );
}
